import { defineComponent } from 'vue';
import { Extension } from '@tiptap/core';
import { Editor, EditorContent } from '@tiptap/vue-3';
import {
    QBtn,
    QSeparator,
    QSelect,
    QMenu,
    QItem,
    QItemSection,
    QItemLabel,
    QInput,
    QIcon,
} from 'quasar';
import { buildHtmlEditorExtensions, buildFontFamilySelectOptions } from './richText/tiptap/buildHtmlEditorExtensions.js';
import {
    EDITOR_COLOR_PALETTE,
    RECENT_TEXT_COLORS_LS_KEY,
    RECENT_BG_COLORS_LS_KEY,
    loadStoredColorList,
    rememberCustomColor,
    normalizeHex,
} from './richText/tiptap/htmlEditorColorPalette.js';
import { buildFontSizeSelectOptions } from './richText/tiptap/htmlEditorFontSizes.js';
import { ROOSTER_EMOJI_PICKER_CHARS } from './richText/tiptap/roosterEmojiPickerData.js';
import { getItemInsertPayload } from './richText/quickText/quickTextKit.js';
import QuickTextMenuShell from './richText/quickText/QuickTextMenuShell.vue';
import './schemaform/formHtmlEditor.css';

function createEnterToSendExtension(getState) {
    return Extension.create({
        name: 'roosterEnterToSend',
        addKeyboardShortcuts() {
            return {
                Enter: () => {
                    const s = getState();
                    if (!s.showSendButton || s.sendDisabled)
                        return false;
                    s.onSend();
                    return true;
                },
            };
        },
    });
}

export default defineComponent({
    name: 'rooster-tiptap-editor',
    components: {
        EditorContent,
        QuickTextMenuShell,
        QBtn,
        QSeparator,
        QSelect,
        QMenu,
        QItem,
        QItemSection,
        QItemLabel,
        QInput,
        QIcon,
    },
    props: {
        modelValue: { type: String, default: '' },
        readOnly: { type: Boolean, default: false },
        enableFormatting: { type: Boolean, default: true },
        enableImagePicker: { type: Boolean, default: true },
        enableQuickText: { type: Boolean, default: true },
        showSendButton: { type: Boolean, default: false },
        sendDisabled: { type: Boolean, default: false },
    },
    emits: ['update:modelValue', 'send'],
    data: () => ({
        editor: null,
        toolbarRevision: 0,
        hasfocus: false,
        lastEmittedHtml: '',
        menuLink: false,
        linkDraft: '',
        menuImage: false,
        imageSrcDraft: '',
        imageAltDraft: '',
        menuTextColor: false,
        menuBgColor: false,
        menuEmoji: false,
        textColorDraft: '#000000',
        bgColorDraft: '#ffffff',
        recentTextColors: [],
        recentBgColors: [],
        fontFamilyOptions: buildFontFamilySelectOptions(),
        fontSizeOptions: buildFontSizeSelectOptions(),
        _unwatchModel: null,
    }),
    watch: {
        readOnly(ro) {
            if (this.editor)
                this.editor.setEditable(!ro);
        },
        showSendButton() {
            this.toolbarRevision++;
        },
        sendDisabled() {
            this.toolbarRevision++;
        },
        enableFormatting() {
            this.toolbarRevision++;
        },
        enableImagePicker() {
            this.toolbarRevision++;
        },
    },
    mounted() {
        this.recentTextColors = loadStoredColorList(RECENT_TEXT_COLORS_LS_KEY);
        this.recentBgColors = loadStoredColorList(RECENT_BG_COLORS_LS_KEY);

        const getEnterState = () => ({
            showSendButton: this.showSendButton,
            sendDisabled: this.sendDisabled,
            onSend: () => this.onEnterSend(),
        });

        const initial = (this.modelValue ?? '').toString();
        this.editor = new Editor({
            extensions: [
                ...buildHtmlEditorExtensions({ allowBase64Images: true }),
                createEnterToSendExtension(() => getEnterState()),
            ],
            content: initial.trim() ? initial : '<p></p>',
            contentType: 'html',
            editable: !this.readOnly,
            editorProps: {
                attributes: {
                    class: 'form-html-editor-prose tiptap',
                },
            },
            onUpdate: () => {
                if (!this.editor)
                    return;
                const html = this.editor.getHTML();
                this.lastEmittedHtml = html;
                this.$emit('update:modelValue', html);
                this.toolbarRevision++;
            },
            onSelectionUpdate: () => {
                this.toolbarRevision++;
            },
            onTransaction: () => {
                this.toolbarRevision++;
            },
            onFocus: () => {
                this.hasfocus = true;
            },
            onBlur: () => {
                this.hasfocus = false;
                this.flushToParent();
            },
        });
        this.lastEmittedHtml = initial;

        this._unwatchModel = this.$watch(
            () => this.modelValue,
            (val) => {
                if (!this.editor)
                    return;
                const next = (val ?? '').toString();
                if (this.hasfocus)
                    return;
                if (next === this.lastEmittedHtml)
                    return;
                this.editor.commands.setContent(next.trim() ? next : '<p></p>', {
                    contentType: 'html',
                    emitUpdate: false,
                });
                this.lastEmittedHtml = next;
            },
        );
    },
    beforeUnmount() {
        if (this._unwatchModel) {
            this._unwatchModel();
            this._unwatchModel = null;
        }
        // TipTap `destroy()` clears `editorView` immediately. Child `QuickTextMenuShell`
        // still runs `beforeUnmount` after this (Vue parent `beforeUnmount` runs first),
        // so teardown the editor in `unmounted` instead.
    },
    unmounted() {
        if (this.editor) {
            this.editor.destroy();
            this.editor = null;
        }
    },
    methods: {
        getEditor() {
            return this.editor;
        },
        getHTML() {
            return this.editor?.getHTML() || '';
        },
        getPlainText() {
            if (!this.editor)
                return '';
            return this.editor.getText().replace(/\n+$/, '').trim();
        },
        flushToParent() {
            if (!this.editor)
                return;
            const html = this.editor.getHTML();
            this.lastEmittedHtml = html;
            this.$emit('update:modelValue', html);
        },
        onEnterSend() {
            if (!this.editor)
                return;
            this.$emit('send', {
                html: this.editor.getHTML(),
                plainText: this.getPlainText(),
            });
        },
        clearContent() {
            if (!this.editor)
                return;
            this.editor.chain().focus().setContent('<p></p>', { emitUpdate: true }).run();
        },
        insertQuickTextItem(item) {
            const ed = this.editor;
            if (!ed?.view)
                return;
            const { richHtml, plainText } = getItemInsertPayload(item);
            if (!richHtml && !plainText)
                return;
            const { from, to } = ed.state.selection;
            if (from !== to)
                ed.chain().focus().deleteRange({ from, to }).run();
            const pos = from;
            if (richHtml)
                ed.chain().focus().insertContentAt(pos, richHtml, { contentType: 'html' }).run();
            else
                ed.chain().focus().insertContentAt(pos, plainText).run();
        },
        insertPlainText(text) {
            if (!this.editor)
                return;
            this.editor.chain().focus().insertContent(text || '').run();
        },
        insertEmoji(ch) {
            if (!this.editor || this.tbDisabled())
                return;
            this.editor.chain().focus().insertContent(ch).run();
            this.menuEmoji = false;
            this.toolbarRevision++;
        },
        chain() {
            return this.editor?.chain().focus();
        },
        tbDisabled() {
            return this.readOnly || !this.editor;
        },
        prepareLinkMenu() {
            if (!this.editor)
                return;
            const prev = this.editor.getAttributes('link').href || '';
            this.linkDraft = typeof prev === 'string' ? prev : '';
        },
        applyLink() {
            const url = (this.linkDraft || '').trim();
            if (!this.editor)
                return;
            if (!url)
                this.editor.chain().focus().extendMarkRange('link').unsetLink().run();
            else
                this.editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
            this.menuLink = false;
        },
        prepareImageMenu() {
            if (!this.editor)
                return;
            const attrs = this.editor.getAttributes('image');
            this.imageSrcDraft = typeof attrs.src === 'string' ? attrs.src : '';
            this.imageAltDraft = typeof attrs.alt === 'string' ? attrs.alt : '';
        },
        applyImage() {
            const src = (this.imageSrcDraft || '').trim();
            const alt = (this.imageAltDraft || '').trim();
            if (!this.editor)
                return;
            if (!src) {
                if (this.editor.isActive('image'))
                    this.editor.chain().focus().deleteNode('image').run();
            }
            else if (this.editor.isActive('image'))
                this.editor.chain().focus().updateAttributes('image', { src, alt: alt || null }).run();
            else
                this.editor.chain().focus().setImage({ src, alt: alt || undefined }).run();
            this.menuImage = false;
        },
        removeImage() {
            if (!this.editor)
                return;
            if (this.editor.isActive('image'))
                this.editor.chain().focus().deleteNode('image').run();
            this.menuImage = false;
        },
        prepareColorMenus() {
            const ts = this.editor?.getAttributes('textStyle') || {};
            this.textColorDraft = normalizeHex(ts.color) || '#000000';
            this.bgColorDraft = normalizeHex(ts.backgroundColor) || '#ffffff';
        },
        applyTextColor(hex) {
            const h = normalizeHex(hex);
            if (!h)
                return;
            this.textColorDraft = h;
            if (!this.editor)
                return;
            if (h === '#000000')
                this.editor.chain().focus().unsetColor().run();
            else
                this.editor.chain().focus().setColor(h).run();
        },
        applyBgColor(hex) {
            const h = normalizeHex(hex);
            if (!h)
                return;
            this.bgColorDraft = h;
            if (!this.editor)
                return;
            if (h === '#ffffff')
                this.editor.chain().focus().unsetBackgroundColor().run();
            else
                this.editor.chain().focus().setBackgroundColor(h).run();
        },
        onNativeTextColorInput(e) {
            const hex = normalizeHex(e?.target?.value);
            if (!hex)
                return;
            this.textColorDraft = hex;
            this.recentTextColors = rememberCustomColor(hex, this.recentTextColors, RECENT_TEXT_COLORS_LS_KEY);
            this.applyTextColor(hex);
            this.menuTextColor = false;
            this.toolbarRevision++;
        },
        onNativeBgColorInput(e) {
            const hex = normalizeHex(e?.target?.value);
            if (!hex)
                return;
            this.bgColorDraft = hex;
            this.recentBgColors = rememberCustomColor(hex, this.recentBgColors, RECENT_BG_COLORS_LS_KEY);
            this.applyBgColor(hex);
            this.menuBgColor = false;
            this.toolbarRevision++;
        },
        sinkListItem() {
            const c = this.chain();
            const ed = this.editor;
            if (!c || !ed)
                return;
            if (ed.can().sinkListItem('listItem'))
                c.sinkListItem('listItem').run();
        },
        liftListItem() {
            const c = this.chain();
            const ed = this.editor;
            if (!c || !ed)
                return;
            if (ed.can().liftListItem('listItem'))
                c.liftListItem('listItem').run();
        },
        canSinkListItem() {
            const ed = this.editor;
            if (!ed)
                return false;
            return ed.can().sinkListItem('listItem');
        },
        canLiftListItem() {
            const ed = this.editor;
            if (!ed)
                return false;
            return ed.can().liftListItem('listItem');
        },
        currentFontFamilyValue() {
            const ed = this.editor;
            if (!ed)
                return '';
            const ff = ed.getAttributes('textStyle').fontFamily;
            return typeof ff === 'string' ? ff : '';
        },
        onFontFamilySelect(value) {
            const c = this.chain();
            if (!c)
                return;
            if (!value)
                c.unsetFontFamily().run();
            else
                c.setFontFamily(value).run();
        },
        currentFontSizeValue() {
            const ed = this.editor;
            if (!ed)
                return '';
            const fs = ed.getAttributes('textStyle').fontSize;
            return typeof fs === 'string' ? fs : '';
        },
        onFontSizeSelect(value) {
            const c = this.chain();
            if (!c)
                return;
            if (!value)
                c.unsetFontSize().run();
            else
                c.setFontSize(value).run();
        },
        pickTextPaletteColor(hex) {
            this.applyTextColor(hex);
            this.menuTextColor = false;
        },
        pickBgPaletteColor(hex) {
            this.applyBgColor(hex);
            this.menuBgColor = false;
        },
        clearBackgroundColorMenu() {
            if (!this.editor)
                return;
            this.editor.chain().focus().unsetBackgroundColor().run();
            this.menuBgColor = false;
        },
        renderToolbar() {
            const ed = this.editor;
            const dis = this.tbDisabled();
            void this.toolbarRevision;

            const iconBtn = (icon, onClick, active = false, extra = {}) => {
                const { disable: extraDisable, ...rest } = extra;
                return (
                    <QBtn
                        flat={!active}
                        unelevated={active}
                        dense
                        round
                        size="sm"
                        color={active ? 'primary' : 'grey-8'}
                        icon={icon}
                        disable={dis || !!extraDisable}
                        onClick={onClick}
                        {...rest}
                    />
                );
            };

            const fontFamilySelectSlots = {
                option: (scope) => {
                    const family = scope.opt?.value ? String(scope.opt.value) : 'inherit';
                    return (
                        <QItem {...scope.itemProps}>
                            <QItemSection>
                                <QItemLabel style={{ fontFamily: family }}>{scope.opt?.label}</QItemLabel>
                            </QItemSection>
                        </QItem>
                    );
                },
                'selected-item': (scope) => {
                    const family = scope.opt?.value ? String(scope.opt.value) : 'inherit';
                    return (
                        <span class="ellipsis" style={{ fontFamily: family }}>{scope.opt?.label}</span>
                    );
                },
            };

            return (
                <div class="form-html-editor-toolbar row items-center no-wrap">
                    {iconBtn('mdi-undo', () => this.chain()?.undo().run(), false, {
                        disable: dis || !ed?.can().undo(),
                        title: 'Undo',
                        'aria-label': 'Undo',
                    })}
                    {iconBtn('mdi-redo', () => this.chain()?.redo().run(), false, {
                        disable: dis || !ed?.can().redo(),
                        title: 'Redo',
                        'aria-label': 'Redo',
                    })}
                    <QSeparator vertical inset class="q-mx-xs" />
                    {iconBtn('mdi-format-bold', () => this.chain()?.toggleBold().run(), ed?.isActive('bold'), {
                        title: 'Bold',
                        'aria-label': 'Bold',
                    })}
                    {iconBtn('mdi-format-italic', () => this.chain()?.toggleItalic().run(), ed?.isActive('italic'), {
                        title: 'Italic',
                        'aria-label': 'Italic',
                    })}
                    {iconBtn('mdi-format-underline', () => this.chain()?.toggleUnderline().run(), ed?.isActive('underline'), {
                        title: 'Underline',
                        'aria-label': 'Underline',
                    })}
                    {iconBtn('mdi-format-strikethrough', () => this.chain()?.toggleStrike().run(), ed?.isActive('strike'), {
                        title: 'Strikethrough',
                        'aria-label': 'Strikethrough',
                    })}
                    <QSeparator vertical inset class="q-mx-xs" />
                    <div class="row items-center no-wrap q-gutter-xs">
                        <QIcon name="mdi-format-font" size="xs" color="grey-7" aria-hidden="true" />
                        <QSelect
                            dense
                            borderless
                            options-dense
                            emit-value
                            map-options
                            options={this.fontFamilyOptions}
                            modelValue={this.currentFontFamilyValue()}
                            disable={dis}
                            color={this.currentFontFamilyValue() ? 'primary' : 'grey-8'}
                            onUpdate:modelValue={(v) => this.onFontFamilySelect(v)}
                            title="Font family"
                            aria-label="Font family"
                            popup-content-class="form-html-editor-font-select-popup"
                            style={{ minWidth: '7.5rem' }}
                        >
                            {fontFamilySelectSlots}
                        </QSelect>
                    </div>
                    <div class="row items-center no-wrap q-gutter-xs">
                        <QIcon name="mdi-format-size" size="xs" color="grey-7" aria-hidden="true" />
                        <QSelect
                            dense
                            borderless
                            options-dense
                            emit-value
                            map-options
                            options={this.fontSizeOptions}
                            modelValue={this.currentFontSizeValue()}
                            disable={dis}
                            color={this.currentFontSizeValue() ? 'primary' : 'grey-8'}
                            onUpdate:modelValue={(v) => this.onFontSizeSelect(v)}
                            title="Font size"
                            aria-label="Font size"
                            style={{ minWidth: '5rem' }}
                        />
                    </div>
                    <QSeparator vertical inset class="q-mx-xs" />
                    <QBtn
                        flat={!ed?.isActive('link')}
                        unelevated={!!ed?.isActive('link')}
                        dense
                        round
                        size="sm"
                        color={ed?.isActive('link') ? 'primary' : 'grey-8'}
                        icon="mdi-link-variant"
                        disable={dis}
                        title="Insert or edit link"
                        aria-label="Insert or edit link"
                    >
                        <QMenu
                            modelValue={this.menuLink}
                            onUpdate:modelValue={(v) => { this.menuLink = v; }}
                            onBeforeShow={() => this.prepareLinkMenu()}
                        >
                            <div class="q-pa-md" style={{ minWidth: '260px' }}>
                                <QInput dense outlined label="URL" modelValue={this.linkDraft} onUpdate:modelValue={(v) => { this.linkDraft = v; }} />
                                <div class="row justify-end q-gutter-sm q-mt-sm">
                                    <QBtn flat label="Remove" color="negative" size="sm" onClick={() => { this.linkDraft = ''; this.applyLink(); }} />
                                    <QBtn unelevated color="primary" label="Apply" size="sm" onClick={() => this.applyLink()} />
                                </div>
                            </div>
                        </QMenu>
                    </QBtn>
                    {iconBtn('mdi-link-variant-off', () => this.chain()?.extendMarkRange('link').unsetLink().run(), false, {
                        disable: dis || !ed?.isActive('link'),
                        title: 'Remove link',
                        'aria-label': 'Remove link',
                    })}
                    {this.enableImagePicker
                        ? (
                            <QBtn
                                flat={!ed?.isActive('image')}
                                unelevated={!!ed?.isActive('image')}
                                dense
                                round
                                size="sm"
                                color={ed?.isActive('image') ? 'primary' : 'grey-8'}
                                icon="mdi-image-outline"
                                disable={dis}
                                title="Insert or edit image"
                                aria-label="Insert or edit image"
                            >
                                <QMenu
                                    modelValue={this.menuImage}
                                    onUpdate:modelValue={(v) => { this.menuImage = v; }}
                                    onBeforeShow={() => this.prepareImageMenu()}
                                >
                                    <div class="q-pa-md" style={{ minWidth: '280px' }}>
                                        <QInput dense outlined class="q-mb-sm" label="Image URL" modelValue={this.imageSrcDraft} onUpdate:modelValue={(v) => { this.imageSrcDraft = v; }} />
                                        <QInput dense outlined label="Alt text" modelValue={this.imageAltDraft} onUpdate:modelValue={(v) => { this.imageAltDraft = v; }} />
                                        <div class="row justify-end q-gutter-sm q-mt-sm">
                                            <QBtn flat label="Remove" color="negative" size="sm" disable={!ed?.isActive('image')} onClick={() => this.removeImage()} />
                                            <QBtn unelevated color="primary" label="Apply" size="sm" onClick={() => this.applyImage()} />
                                        </div>
                                    </div>
                                </QMenu>
                            </QBtn>
                        )
                        : null}
                    <QSeparator vertical inset class="q-mx-xs" />
                    {iconBtn('mdi-format-list-bulleted', () => this.chain()?.toggleBulletList().run(), ed?.isActive('bulletList'), {
                        title: 'Bullet list',
                        'aria-label': 'Bullet list',
                    })}
                    {iconBtn('mdi-format-list-numbered', () => this.chain()?.toggleOrderedList().run(), ed?.isActive('orderedList'), {
                        title: 'Numbered list',
                        'aria-label': 'Numbered list',
                    })}
                    {iconBtn('mdi-format-indent-decrease', () => this.liftListItem(), false, {
                        disable: dis || !this.canLiftListItem(),
                        title: 'Outdent list item',
                        'aria-label': 'Outdent list item',
                    })}
                    {iconBtn('mdi-format-indent-increase', () => this.sinkListItem(), false, {
                        disable: dis || !this.canSinkListItem(),
                        title: 'Indent list item',
                        'aria-label': 'Indent list item',
                    })}
                    <QSeparator vertical inset class="q-mx-xs" />
                    {iconBtn('mdi-format-quote-close', () => this.chain()?.toggleBlockquote().run(), ed?.isActive('blockquote'), {
                        title: 'Blockquote',
                        'aria-label': 'Blockquote',
                    })}
                    {iconBtn('mdi-code-braces-box', () => this.chain()?.toggleCodeBlock().run(), ed?.isActive('codeBlock'), {
                        title: 'Code block',
                        'aria-label': 'Code block',
                    })}
                    <QSeparator vertical inset class="q-mx-xs" />
                    <QBtn
                        flat
                        dense
                        round
                        size="sm"
                        color="grey-8"
                        icon="mdi-format-color-text"
                        disable={dis}
                        title="Text color"
                        aria-label="Text color"
                    >
                        <QMenu
                            modelValue={this.menuTextColor}
                            onUpdate:modelValue={(v) => { this.menuTextColor = v; }}
                            onBeforeShow={() => this.prepareColorMenus()}
                        >
                            <div class="q-pa-sm form-html-editor-color-menu">
                                <div class="text-caption text-grey-7 q-mb-xs">Text color</div>
                                <div class="row q-col-gutter-xs">
                                    {EDITOR_COLOR_PALETTE.map((hex) => (
                                        <div class="col-2" key={hex}>
                                            <QBtn
                                                dense
                                                flat
                                                class="form-html-editor-color-swatch"
                                                style={{ backgroundColor: hex }}
                                                disable={dis}
                                                title={hex}
                                                aria-label={`Text color ${hex}`}
                                                onClick={() => this.pickTextPaletteColor(hex)}
                                            />
                                        </div>
                                    ))}
                                </div>
                                {this.recentTextColors.length
                                    ? (
                                        <div>
                                            <div class="text-caption text-grey-7 q-mt-sm q-mb-xs">Recently used</div>
                                            <div class="row q-col-gutter-xs">
                                                {this.recentTextColors.map((hex) => (
                                                    <div class="col-2" key={`rt-${hex}`}>
                                                        <QBtn
                                                            dense
                                                            flat
                                                            class="form-html-editor-color-swatch form-html-editor-color-swatch--recent"
                                                            style={{ backgroundColor: hex }}
                                                            disable={dis}
                                                            title={hex}
                                                            aria-label={`Recent text color ${hex}`}
                                                            onClick={() => this.pickTextPaletteColor(hex)}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )
                                    : null}
                                <div class="form-html-editor-color-native-wrap">
                                    <QBtn flat dense no-caps label="Custom color…" color="grey-8" class="fit" disable={dis} />
                                    <input
                                        type="color"
                                        value={this.textColorDraft}
                                        onInput={(e) => this.onNativeTextColorInput(e)}
                                        aria-label="Choose custom text color"
                                    />
                                </div>
                            </div>
                        </QMenu>
                    </QBtn>
                    <QBtn
                        flat
                        dense
                        round
                        size="sm"
                        color="grey-8"
                        icon="mdi-format-color-fill"
                        disable={dis}
                        title="Background color"
                        aria-label="Background color"
                    >
                        <QMenu
                            modelValue={this.menuBgColor}
                            onUpdate:modelValue={(v) => { this.menuBgColor = v; }}
                            onBeforeShow={() => this.prepareColorMenus()}
                        >
                            <div class="q-pa-sm form-html-editor-color-menu">
                                <div class="text-caption text-grey-7 q-mb-xs">Background</div>
                                <div class="row q-mb-xs">
                                    <QBtn
                                        flat
                                        dense
                                        no-caps
                                        size="sm"
                                        color="grey-8"
                                        label="No background"
                                        disable={dis}
                                        class="full-width"
                                        onClick={() => this.clearBackgroundColorMenu()}
                                    />
                                </div>
                                <div class="row q-col-gutter-xs">
                                    {EDITOR_COLOR_PALETTE.map((hex) => (
                                        <div class="col-2" key={hex}>
                                            <QBtn
                                                dense
                                                flat
                                                class="form-html-editor-color-swatch"
                                                style={{ backgroundColor: hex }}
                                                disable={dis}
                                                title={hex}
                                                aria-label={`Background ${hex}`}
                                                onClick={() => this.pickBgPaletteColor(hex)}
                                            />
                                        </div>
                                    ))}
                                </div>
                                {this.recentBgColors.length
                                    ? (
                                        <div>
                                            <div class="text-caption text-grey-7 q-mt-sm q-mb-xs">Recently used</div>
                                            <div class="row q-col-gutter-xs">
                                                {this.recentBgColors.map((hex) => (
                                                    <div class="col-2" key={`rb-${hex}`}>
                                                        <QBtn
                                                            dense
                                                            flat
                                                            class="form-html-editor-color-swatch form-html-editor-color-swatch--recent"
                                                            style={{ backgroundColor: hex }}
                                                            disable={dis}
                                                            title={hex}
                                                            aria-label={`Recent background ${hex}`}
                                                            onClick={() => this.pickBgPaletteColor(hex)}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )
                                    : null}
                                <div class="form-html-editor-color-native-wrap">
                                    <QBtn flat dense no-caps label="Custom color…" color="grey-8" class="fit" disable={dis} />
                                    <input
                                        type="color"
                                        value={this.bgColorDraft}
                                        onInput={(e) => this.onNativeBgColorInput(e)}
                                        aria-label="Choose custom background color"
                                    />
                                </div>
                            </div>
                        </QMenu>
                    </QBtn>
                    <QSeparator vertical inset class="q-mx-xs" />
                    {iconBtn('mdi-format-align-left', () => this.chain()?.setTextAlign('left').run(), ed?.isActive({ textAlign: 'left' }), {
                        title: 'Align left',
                        'aria-label': 'Align left',
                    })}
                    {iconBtn('mdi-format-align-center', () => this.chain()?.setTextAlign('center').run(), ed?.isActive({ textAlign: 'center' }), {
                        title: 'Align center',
                        'aria-label': 'Align center',
                    })}
                    {iconBtn('mdi-format-align-right', () => this.chain()?.setTextAlign('right').run(), ed?.isActive({ textAlign: 'right' }), {
                        title: 'Align right',
                        'aria-label': 'Align right',
                    })}
                    <QSeparator vertical inset class="q-mx-xs" />
                    <QBtn
                        flat
                        dense
                        round
                        size="sm"
                        color="grey-8"
                        icon="mdi-emoticon-happy-outline"
                        disable={dis}
                        title="Emoji"
                        aria-label="Emoji"
                    >
                        <QMenu
                            modelValue={this.menuEmoji}
                            onUpdate:modelValue={(v) => { this.menuEmoji = v; }}
                            class="rooster-emoji-menu"
                        >
                            <div class="q-pa-sm rooster-emoji-popup">
                                <div class="text-caption text-grey-7 q-mb-xs">Insert emoji</div>
                                <div class="row q-col-gutter-xs">
                                    {ROOSTER_EMOJI_PICKER_CHARS.map((ch, i) => (
                                        <div class="col-2" key={`e-${i}-${ch}`}>
                                            <QBtn
                                                flat
                                                dense
                                                class="rooster-emoji-cell"
                                                disable={dis}
                                                aria-label={`Insert ${ch}`}
                                                onClick={() => this.insertEmoji(ch)}
                                            >
                                                <span style={{ fontSize: '1.35rem', lineHeight: 1 }}>{ch}</span>
                                            </QBtn>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </QMenu>
                    </QBtn>
                    <QSeparator vertical inset class="q-mx-xs" />
                    {iconBtn('mdi-format-clear', () => {
                        this.chain()?.unsetAllMarks().run();
                    }, false, {
                        title: 'Clear formatting',
                        'aria-label': 'Clear formatting',
                    })}
                    {this.enableQuickText ? <QSeparator vertical inset class="q-mx-xs" /> : null}
                    {this.enableQuickText ? iconBtn('mdi-pound', () => {
                        this.$refs.quickTextShell?.openFromHashtag?.();
                    }, false, {
                        title: 'Quick text',
                        'aria-label': 'Quick text',
                    }) : null}
                </div>
            );
        },
    },
    render() {
        void this.toolbarRevision;
        const toolbar = this.enableFormatting ? this.renderToolbar() : null;
        return (
            <div
                class="rooster-tiptap-root"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: '1 1 auto',
                    minHeight: 0,
                    overflow: 'hidden',
                }}
            >
                {toolbar}
                <div class="form-html-editor-body">
                    {this.editor ? <EditorContent editor={this.editor} /> : null}
                </div>
                {this.enableQuickText
                    ? <QuickTextMenuShell ref="quickTextShell" editor={this.editor} enabled={!this.readOnly} />
                    : null}
            </div>
        );
    },
});
